// The canonical backbone: a small, FIXED, centrally-defined set of qualities the
// future network will propagate on. NOT user-editable. A user's own dimension
// may map to AT MOST ONE of these keys (or to null, "subjective-only"). Nothing
// reads canonical_key for ranking yet; it is persisted for the network phase.
//
// The set evolves only by deliberate code change, never by user input. Bump
// CANONICAL_VERSION when the set changes so a later mapping phase can reconcile.

export const CANONICAL_VERSION = 1;

export interface CanonicalQuality {
  key: string;
  name: string;
  definition: string;
  // Warmth is relationship closeness, deliberately kept apart from the trust
  // qualities. Flagged so the UI can label it "relationship, not a quality".
  relationship?: boolean;
}

export const CANONICAL_QUALITIES: readonly CanonicalQuality[] = [
  { key: "judgement", name: "Judgement", definition: "Quality of decisions and reads under uncertainty." },
  { key: "integrity", name: "Integrity", definition: "Straight dealing; honours commitments and terms." },
  { key: "reliability", name: "Reliability", definition: "Follows through; does what they say, when they say." },
  { key: "competence", name: "Competence", definition: "Domain and technical skill, and analytical rigour." },
  { key: "sourcing", name: "Sourcing", definition: "Origination instinct; reads and referrals worth weighting." },
  { key: "financial-credibility", name: "Financial credibility", definition: "Capital and funding is real and committed." },
  { key: "responsiveness", name: "Responsiveness", definition: "Moves promptly; runs a clean, disciplined process." },
  { key: "discretion", name: "Discretion", definition: "Handles sensitive information appropriately." },
  { key: "influence", name: "Influence", definition: "Standing that moves outcomes among others." },
  { key: "warmth", name: "Warmth", definition: "Relationship closeness (kept separate from the qualities).", relationship: true },
];

const BY_KEY = new Map(CANONICAL_QUALITIES.map((q) => [q.key, q]));

export function canonicalByKey(key: string | null): CanonicalQuality | undefined {
  return key === null ? undefined : BY_KEY.get(key);
}

// A key is valid iff it is null (subjective-only) or names a backbone quality.
export function isValidCanonicalKey(key: string | null): boolean {
  return key === null || BY_KEY.has(key);
}
